import React, { useEffect, useState } from "react";
import { ServiceBase } from "../../utilities/services/service-base";

interface ApiKeyItem {
    id: string;
    organizationName: string;
    isEnabled: boolean;
    keyPreview: string;
}

interface UsageResponse {
    apiKeyId: string;
    organizationName: string;
    period: { start: string; end: string };
    lifetimeCounters: {
        groupCalculated: number;
        mskRiskCalculated: number;
        mskHealthCalculated: number;
        mskRiskRetestCalculated: number;
        mskHealthRetestCalculated: number;
    };
    groupCalculated: number;
    completed: { risk: number; health: number; total: number };
    retests: { risk: number; health: number; total: number };
    totalEvents: number;
}

function toDateInputValue(date: Date): string {
    return date.toISOString().slice(0, 10);
}

const COMPONENT_CLASS = "c-api-key-usage-tool";

const ApiKeyUsageTool: React.FC = () => {
    const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>([]);
    const [selectedKeyId, setSelectedKeyId] = useState("");
    const [startDate, setStartDate] = useState(toDateInputValue(new Date(new Date().setMonth(new Date().getMonth() - 1))));
    const [endDate, setEndDate] = useState(toDateInputValue(new Date()));
    const [usage, setUsage] = useState<UsageResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const keys = await ServiceBase.list<ApiKeyItem[]>("/admin/api-keys")();
                setApiKeys(keys ?? []);
            } catch {
                setError("Failed to load API keys.");
            }
        })();
    }, []);

    async function fetchUsage() {
        if (!selectedKeyId) { setError("Select an API key."); return; }
        setLoading(true);
        setError("");
        setUsage(null);
        try {
            const result = await ServiceBase.list<UsageResponse, { apiKeyId: string; startDate: string; endDate: string }>("/admin/api-key-usage")({
                apiKeyId: selectedKeyId,
                startDate,
                endDate,
            });
            setUsage(result);
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || "Failed to load usage.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className={COMPONENT_CLASS} style={{ maxWidth: 800 }}>
            <h3>API Key Usage</h3>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

                <div>
                    <label>API Key</label>
                    <select
                        value={selectedKeyId}
                        onChange={(e) => setSelectedKeyId(e.target.value)}
                        style={{ width: "100%", padding: 8 }}
                    >
                        <option value="">Select an API key</option>
                        {apiKeys.map((k) => (
                            <option key={k.id} value={k.id}>
                                {k.organizationName || k.keyPreview || k.id}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                    <div>
                        <label>From</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ display: "block", padding: 6 }}
                        />
                    </div>
                    <div>
                        <label>To</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ display: "block", padding: 6 }}
                        />
                    </div>
                </div>

                <button onClick={fetchUsage} disabled={loading} style={{ padding: "8px 16px", alignSelf: "flex-start" }}>
                    {loading ? "Loading..." : "Get Usage"}
                </button>

                {error && <div style={{ color: "#b91c1c" }}>{error}</div>}

                {usage && (
                    <div>
                        <h4>Usage for {usage.organizationName || usage.apiKeyId}</h4>
                        <p style={{ color: "#666" }}>{usage.period.start.slice(0, 10)} — {usage.period.end.slice(0, 10)}</p>

                        <h5>Lifetime Counters</h5>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#f3f4f6" }}>
                                    <th style={thStyle}>Group Calculated</th>
                                    <th style={thStyle}>MSK Risk</th>
                                    <th style={thStyle}>MSK Health</th>
                                    <th style={thStyle}>Risk Retests</th>
                                    <th style={thStyle}>Health Retests</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={tdStyle}>{usage.lifetimeCounters.groupCalculated}</td>
                                    <td style={tdStyle}>{usage.lifetimeCounters.mskRiskCalculated}</td>
                                    <td style={tdStyle}>{usage.lifetimeCounters.mskHealthCalculated}</td>
                                    <td style={tdStyle}>{usage.lifetimeCounters.mskRiskRetestCalculated}</td>
                                    <td style={tdStyle}>{usage.lifetimeCounters.mskHealthRetestCalculated}</td>
                                </tr>
                            </tbody>
                        </table>

                        <h5 style={{ marginTop: 16 }}>Period Usage ({usage.period.start.slice(0, 10)} — {usage.period.end.slice(0, 10)})</h5>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#f3f4f6" }}>
                                    <th style={thStyle}>Group Calculated</th>
                                    <th style={thStyle}>Completed (Risk)</th>
                                    <th style={thStyle}>Completed (Health)</th>
                                    <th style={thStyle}>Completed (Total)</th>
                                    <th style={thStyle}>Retests (Risk)</th>
                                    <th style={thStyle}>Retests (Health)</th>
                                    <th style={thStyle}>Retests (Total)</th>
                                    <th style={thStyle}>Total Events</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={tdStyle}>{usage.groupCalculated}</td>
                                    <td style={tdStyle}>{usage.completed.risk}</td>
                                    <td style={tdStyle}>{usage.completed.health}</td>
                                    <td style={tdStyle}>{usage.completed.total}</td>
                                    <td style={tdStyle}>{usage.retests.risk}</td>
                                    <td style={tdStyle}>{usage.retests.health}</td>
                                    <td style={tdStyle}>{usage.retests.total}</td>
                                    <td style={tdStyle}>{usage.totalEvents}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const thStyle: React.CSSProperties = {
    border: "1px solid #d1d5db", padding: "6px 10px", textAlign: "left", fontSize: 13,
};
const tdStyle: React.CSSProperties = {
    border: "1px solid #d1d5db", padding: "6px 10px", fontSize: 13,
};

export default ApiKeyUsageTool;
